import React, { useEffect, useMemo, useState } from "react";
import {
  X,
  FileText,
  Package,
  Banknote,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  RotateCcw,
  Megaphone,
  MessageCircle,
} from "lucide-react";

const ICON_MAP = {
  "file-text": FileText,
  package: Package,
  banknote: Banknote,
  "shield-check": ShieldCheck,
  "check-circle": CheckCircle2,
  "x-circle": XCircle,
  "rotate-ccw": RotateCcw,
  megaphone: Megaphone,
  message: MessageCircle,
};

// WhatsApp-green accent mixed with the app brand (#DF6B38 orange)
const ICON_GRADIENTS = {
  "file-text": "linear-gradient(135deg, #16314C, #2d5a86)",
  package: "linear-gradient(135deg, #7c3aed, #a855f7)",
  banknote: "linear-gradient(135deg, #16a34a, #22c55e)",
  "shield-check": "linear-gradient(135deg, #DF6B38, #DF383F)",
  "check-circle": "linear-gradient(135deg, #16a34a, #22c55e)",
  "x-circle": "linear-gradient(135deg, #dc2626, #ef4444)",
  "rotate-ccw": "linear-gradient(135deg, #d97706, #f59e0b)",
  megaphone: "linear-gradient(135deg, #2563eb, #3b82f6)",
  message: "linear-gradient(135deg, #25D366, #128C7E)",
};

const formatTime = (iso) => {
  if (!iso) return "now";
  const date = new Date(iso);
  const diff = (Date.now() - date.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const getInitials = (name) => {
  if (!name) return "ER";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "ER";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/**
 * WhatsApp-style message popup
 * Shows a small notification card (avatar, sender, message preview) that
 * auto-dismisses after a few seconds. Clicking navigates to the action URL.
 */
const WhatsAppStylePopup = ({ popup, onClose, onOpen, duration = 8000 }) => {
  const [progress, setProgress] = useState(100);
  const [leaving, setLeaving] = useState(false);

  const Icon = ICON_MAP[popup.icon] || Megaphone;
  const initials = useMemo(() => getInitials(popup.sender), [popup.sender]);

  useEffect(() => {
    const dismissTimer = setTimeout(handleClose, duration);
    const start = Date.now();
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - start;
      setProgress(Math.max(0, 100 - (elapsed / duration) * 100));
    }, 50);

    return () => {
      clearTimeout(dismissTimer);
      clearInterval(progressInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration]);

  const handleClose = () => {
    if (leaving) return;
    setLeaving(true);
    setTimeout(onClose, 300);
  };

  const handleClick = () => {
    if (popup.url && onOpen) onOpen(popup);
    handleClose();
  };

  return (
    <div
      className={`relative w-[340px] sm:w-[380px] bg-white rounded-2xl shadow-2xl overflow-hidden cursor-pointer select-none transform transition-all duration-300 ${
        leaving ? "translate-x-[120%] opacity-0" : "translate-x-0 opacity-100"
      }`}
      style={{
        boxShadow:
          "0 16px 48px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.06), 0 4px 12px rgba(22,49,76,0.12)",
        animation: "waPopupIn 0.35s cubic-bezier(0.21, 1.02, 0.73, 1) both",
      }}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && handleClick()}
    >
      {/* Top accent bar */}
      <div
        className="h-1.5 w-full"
        style={{
          background: `linear-gradient(90deg, #25D366 0%, #128C7E 40%, #DF6B38 100%)`,
        }}
      />

      <div className="p-3.5">
        <div className="flex items-start gap-3">
          {/* Avatar */}
          <div className="relative flex-shrink-0">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md"
              style={{
                background: ICON_GRADIENTS[popup.icon] || "linear-gradient(135deg, #16314C, #2d5a86)",
              }}
            >
              {initials}
            </div>
            {/* Status badge icon */}
            <div
              className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-white flex items-center justify-center shadow"
              style={{ border: "1.5px solid #fff" }}
            >
              <Icon className="w-3 h-3" style={{ color: "#128C7E" }} strokeWidth={2.5} />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <p className="text-[15px] font-bold text-gray-900 truncate leading-snug">
                {popup.title || popup.sender}
              </p>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleClose();
                }}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors p-0.5 rounded-full hover:bg-gray-100"
                aria-label="Dismiss notification"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-[13px] font-semibold text-gray-800 truncate mt-0.5">
              {popup.sender}
            </p>

            <p className="text-[13px] text-gray-500 mt-0.5 line-clamp-2 leading-snug">
              {popup.message}
            </p>

            <div className="flex items-center justify-between mt-1.5">
              <span className="text-[11px] text-gray-400 font-medium">
                {formatTime(popup.createdAt)}
              </span>
              <span className="flex items-center gap-1 text-[11px] font-semibold" style={{ color: "#DF6B38" }}>
                <Megaphone className="w-3 h-3" />
                ERP
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-[3px] w-full bg-gray-100">
        <div
          className="h-full transition-all duration-100 ease-linear"
          style={{
            width: `${progress}%`,
            background: "linear-gradient(90deg, #25D366, #128C7E)",
          }}
        />
      </div>

      <style>{`
        @keyframes waPopupIn {
          0% { opacity: 0; transform: translateX(120%) scale(0.95); }
          100% { opacity: 1; transform: translateX(0) scale(1); }
        }
      `}</style>
    </div>
  );
};

export default WhatsAppStylePopup;
